import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag } from 'lucide-react';
import { currencyFmt, placeholderImg } from '@/utils/ui';
import { useTranslation } from 'react-i18next';

const StoreTab = ({ products = [], groupCurrency = 'USD', onAddToCart, onBuyNow }) => {
  const { t } = useTranslation();

  if (!products.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <ShoppingBag className="mx-auto mb-4 text-slate-400" size={48} />
          <h3 className="text-lg font-semibold mb-2">
            {t('groupSpace.store.emptyTitle', { defaultValue: 'No products yet' })}
          </h3>
          <p className="text-slate-600">
            {t('groupSpace.store.emptySubtitle', { defaultValue: 'The store is currently empty' })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {products.map((p) => (
        <Card key={p.id} data-testid={`product-card-${p.id}`}>
          <CardHeader className="p-0">
            <img
              src={p.image || placeholderImg}
              alt={p.name}
              className="w-full h-48 object-cover rounded-t-lg"
              onError={(e) => {
                const img = e.target;
                img.src = placeholderImg;
              }}
            />
          </CardHeader>

          <CardContent className="pt-4">
            <CardTitle className="font-semibold text-lg mb-1">
              {p.name}
            </CardTitle>

            <p className="text-sm text-slate-600 mb-3 line-clamp-2">
              {p.description}
            </p>

            <div className="flex justify-between items-center">
              <span className="font-bold text-lg">
                {currencyFmt(p.price, p.currency || groupCurrency || 'USD')}
              </span>

              {p.stock != null && (
                <Badge variant={p.stock > 0 ? 'default' : 'destructive'}>
                  {p.stock > 0
                    ? t('groupSpace.store.stockLeft', {
                        count: p.stock,
                        defaultValue: '{{count}} left',
                      })
                    : t('groupSpace.store.outOfStock', { defaultValue: 'Out of stock' })}
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                disabled={p.stock === 0}
                onClick={() => onAddToCart(p, 1)}
                data-testid={`add-product-btn-${p.id}`}
              >
                {t('groupSpace.store.addToCart', { defaultValue: 'Add to Cart' })}
              </Button>

              <Button
                size="sm"
                disabled={p.stock === 0}
                onClick={() => onBuyNow(p, 1)}
                data-testid={`buy-product-btn-${p.id}`}
              >
                {t('groupSpace.store.buyNow', { defaultValue: 'Buy Now' })}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default StoreTab;
